import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CurrentUser, LoginBody } from '@flixflow/shared';
import { getMe, login as apiLogin, logout as apiLogout, initCsrf } from '../api/auth.js';

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  login: (body: LoginBody) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initCsrf()
      .then(() => getMe())
      .then((me) => setUser(me))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  async function login(body: LoginBody) {
    const me = await apiLogin(body);
    setUser(me);
  }

  async function logout() {
    await apiLogout();
    setUser(null);
    window.location.href = '/login';
  }

  return <AuthContext.Provider value={{ user, isLoading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
